import React from "react";
import {
  attributesToProps,
  domToReact,
  type DOMNode,
  type Element,
  type HTMLReactParserOptions
} from "html-react-parser";
// The default browser build needs document.implementation, which react4xp SSR (GraalJS) lacks.
import htmlToDom from "html-dom-parser/lib/server/html-to-dom";

import PdfHeading from "./PdfHeading";
import Richtext from "../Richtext/Richtext";

const HEADING_TAG_PATTERN = /^h([1-6])$/;
// Editors write rich text assuming the page title is h1, so h2 is the top heading inside a field.
const RICH_TEXT_TOP_HEADING_LEVEL = 2;

function buildParserOptions(parentHeadingLevel: number): HTMLReactParserOptions {
  const headingOffset = parentHeadingLevel + 1 - RICH_TEXT_TOP_HEADING_LEVEL;

  const parserOptions: HTMLReactParserOptions = {
    replace: (domNode: DOMNode) => {
      if (domNode.type === "comment" && domNode.data.indexOf('#MACRO ') === 0) {
        return <Richtext as="span" text={`<!--${domNode.data}-->`} />;
      }
      if (domNode.type !== "tag") return undefined;

      const element = domNode as Element;
      const headingMatch = HEADING_TAG_PATTERN.exec(element.name);
      if (!headingMatch) return undefined;

      const headingProps = attributesToProps(element.attribs);

      return (
        <PdfHeading
          level={Number(headingMatch[1]) + headingOffset}
          id={headingProps.id as string | undefined}
          className={headingProps.className as string | undefined}
          style={headingProps.style as React.CSSProperties | undefined}
        >
          {domToReact(element.children as DOMNode[], parserOptions)}
        </PdfHeading>
      );
    }
  };

  return parserOptions;
}

type PdfRichTextProps = {
  html?: string | null;
  parentHeadingLevel: number;
  style?: React.CSSProperties;
};

/**
 * Renders CMS rich text with its headings shifted to sit directly below `parentHeadingLevel`.
 */
export default function PdfRichText(props: Readonly<PdfRichTextProps>) {
  const { html, parentHeadingLevel, style } = props;

  if (!html) return null;

  return <div style={style}>{domToReact(htmlToDom(html) as DOMNode[], buildParserOptions(parentHeadingLevel))}</div>;
}
